import { parseServerUtc } from '@/modules/player/lib/clock';

// The TV shows a pairing code and polls until an admin adds it in the console. The code lives
// 10 minutes on the server; an expired or forgotten code is silently replaced with a new one.

export const POLL_INTERVAL_MS = 4000;
export const RETRY_DELAY_MS = 5000;
const STORAGE_KEY = 'pixelplay-pairing';
// Don't resume a saved code that would expire before anyone could type it.
const MIN_REMAINING_MS = 60 * 1000;

export const sleep = (ms, signal) =>
  new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });

// A reload keeps the same code on screen while it is still usable.
export const loadSavedRequest = (storage, now) => {
  try {
    const saved = JSON.parse(storage?.getItem(STORAGE_KEY) ?? 'null');
    return saved && saved.expiresAt - now > MIN_REMAINING_MS ? saved : null;
  } catch {
    return null;
  }
};

const saveRequest = (storage, request) => {
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify(request));
  } catch {
    // Storage unavailable: a reload just shows a new code.
  }
};

const clearRequest = (storage) => {
  try {
    storage?.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

/**
 * Runs until the TV is paired or `signal` aborts.
 * - api.createRequest() → { data: { code, pollToken, expiresAtUtc, pairUrl } }
 * - api.poll(pollToken) → { data: { status: 'pending' | 'expired' | 'paired', screen } }
 * Calls onCode(request) whenever a code is (re)shown, onError(error) when the server can't be
 * reached (it keeps retrying), and onPaired(screen) once with the screen's credentials.
 */
export const runPairing = async ({
  api,
  storage,
  signal,
  onCode,
  onError,
  onPaired,
  now = Date.now,
  wait = sleep,
}) => {
  let request = loadSavedRequest(storage, now());

  while (!signal?.aborted) {
    try {
      if (!request) {
        const { data } = await api.createRequest();
        request = {
          code: data.code,
          pollToken: data.pollToken,
          pairUrl: data.pairUrl,
          expiresAt: parseServerUtc(data.expiresAtUtc),
        };
        saveRequest(storage, request);
      }
      if (signal?.aborted) return;
      onCode(request);

      const { data } = await api.poll(request.pollToken);
      if (signal?.aborted) return;

      if (data.status === 'paired') {
        clearRequest(storage);
        onPaired(data.screen);
        return;
      }
      if (data.status === 'expired') {
        request = null;
        clearRequest(storage);
        continue;
      }
      await wait(POLL_INTERVAL_MS, signal);
    } catch (error) {
      if (signal?.aborted) return;
      // The server no longer knows this request (cleaned up): start over with a new code.
      if (error?.response?.status === 404) {
        request = null;
        clearRequest(storage);
        continue;
      }
      onError(error);
      await wait(RETRY_DELAY_MS, signal);
    }
  }
};
